import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MEXICAN_STATES, buildCafeAddress } from "@/lib/mexican-states";
import { useAppContexts } from "@/hooks/use-app-context";

export const Route = createFileRoute("/_authenticated/cafe/perfil")({
  head: () => ({ meta: [{ title: "Mi cafetería — Brulo" }] }),
  component: CafeProfile,
});


function CafeProfile() {
  const { active: activeCtx, ready: ctxReady } = useAppContexts();
  const ctxCafeId = ctxReady && activeCtx.kind === "cafe" ? activeCtx.id : null;
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [id, setId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    calle: "",
    numero_exterior: "",
    colonia: "",
    codigo_postal: "",
    city: "Aguascalientes",
    estado: "Aguascalientes",
    hours: "",
    description: "",
    member_discount: "",
  });

  useEffect(() => {
    if (!ctxReady) return;
    (async () => {
      setLoading(true);
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      // El café editado se toma del contexto activo; si no hay contexto de café,
      // se resuelve por vínculo de gestión (cafe_managers), nunca por profiles.role.
      let cafeId = ctxCafeId;
      if (!cafeId) {
        const { data: link } = await supabase
          .from("cafe_managers")
          .select("cafe_id")
          .eq("user_id", u.user.id)
          .limit(1)
          .maybeSingle();
        cafeId = link?.cafe_id ?? null;
      }
      const cols =
        "id,name,calle,numero_exterior,colonia,codigo_postal,city,estado,hours,description,member_discount";
      const { data } = cafeId
        ? await supabase.from("cafes").select(cols).eq("id", cafeId).maybeSingle()
        : { data: null };
      if (data) {
        setId(data.id);
        setForm({
          name: data.name ?? "",
          calle: data.calle ?? "",
          numero_exterior: data.numero_exterior ?? "",
          colonia: data.colonia ?? "",
          codigo_postal: data.codigo_postal ?? "",
          city: data.city ?? "Aguascalientes",
          estado: data.estado ?? "Aguascalientes",
          hours: data.hours ?? "",
          description: data.description ?? "",
          member_discount: data.member_discount ?? "",
        });
      } else {
        setId(null);
      }
      setLoading(false);
    })();
  }, [ctxReady, ctxCafeId]);


  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      setSaving(false);
      return;
    }
    const location = buildCafeAddress({
      calle: form.calle,
      numero_exterior: form.numero_exterior,
      colonia: form.colonia,
      codigo_postal: form.codigo_postal,
      city: form.city,
      estado: form.estado,
    });
    const payload = { ...form, location, owner_id: u.user.id };
    const { error } = id
      ? await supabase.from("cafes").update(payload).eq("id", id)
      : await supabase.from("cafes").insert({ ...payload, slug: "" });
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Perfil guardado");
  };

  if (loading) {
    return <div className="mx-auto max-w-2xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 md:px-6 py-12">
      <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">cafetería brulo</p>
      <h1 className="font-display text-5xl mb-10">Mi cafetería</h1>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-lg p-8">
        <Field label="Nombre">
          <input required value={form.name} onChange={set("name")} className={cls} />
        </Field>
        <Field label="Calle">
          <input required value={form.calle} onChange={set("calle")} className={cls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Número exterior">
            <input required value={form.numero_exterior} onChange={set("numero_exterior")} className={cls} />
          </Field>
          <Field label="Código Postal">
            <input
              required
              value={form.codigo_postal}
              onChange={(e) =>
                setForm({ ...form, codigo_postal: e.target.value.replace(/\D/g, "").slice(0, 5) })
              }
              className={cls}
              placeholder="20000"
            />
          </Field>
        </div>
        <Field label="Colonia">
          <input required value={form.colonia} onChange={set("colonia")} className={cls} />
        </Field>
        <Field label="Ciudad">
          <input value={form.city} onChange={set("city")} className={cls} />
        </Field>
        <Field label="Estado">
          <select required value={form.estado} onChange={set("estado")} className={cls}>
            {MEXICAN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Horario">
          <input value={form.hours} onChange={set("hours")} className={cls} placeholder="Lun-Vie 7:00-20:00 · Sáb-Dom 8:00-14:00" />
        </Field>
        <Field label="Descripción">
          <textarea value={form.description} onChange={set("description")} rows={3} className={cls} placeholder="Café de especialidad, desayunos saludables..." />
        </Field>
        <Field label="Descuento para miembros Brulo">
          <input value={form.member_discount} onChange={set("member_discount")} className={cls} placeholder="15% en café tras la salida del domingo" />
        </Field>

        <button
          type="submit"
          disabled={saving}
          className="w-full min-h-12 px-3 py-3 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-semibold tracking-wide hover:opacity-90 disabled:opacity-50 whitespace-normal break-words text-center leading-tight"
        >
          {saving ? "Guardando..." : id ? "Actualizar perfil" : "Crear perfil"}
        </button>
      </form>
    </div>
  );
}

const cls =
  "w-full px-4 py-2.5 bg-input border border-border rounded-md focus:outline-none focus:border-primary transition";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
